import React, {useCallback, useState} from "react";
import {useNavigate} from "react-router-dom";
import axios from "axios";
import {
    CheckboxText,
    CheckboxWrapper,
    Header,
    StyledButton,
    StyledCard,
    StyledForm,
    StyledTextField,
    Subtext
} from "./Register.styled";
import {Checkbox, CircularProgress, IconButton, InputAdornment} from "@mui/material";
import Logo from "../../../assets/images/taia_logo.png";
import {Visibility, VisibilityOff} from "@mui/icons-material";
import {AuthContainer} from "../Auth.styled";
import {usePageTitle} from '../../../hooks/usePageTitle';
import LogoWithLanguageSwitcher from '../../../components/common/LogoWithLanguageSwitcher';
import {PageTitleKeys} from '../../../config/routeConstants';
import {parseError} from "../../../services/http.service";
import {useTranslation} from 'react-i18next';
import {validatePassword} from "../../../utils/password-validation";
import {useSnackbar} from "../../../contexts/SnackbarContext";

async function registerAdmin(email: string, password: string, confirmPassword: string, consent: boolean) {
    try {
        await axios.post(`/auth/register-system-admin`, {
            email,
            password,
            confirmPassword,
            consent
        });
        return true;
    } catch (error) {
        console.error("Błąd rejestracji:", error);
        return false;
    }
}

const RegisterAdmin: React.FC = () => {
    usePageTitle(PageTitleKeys.Register);
    const {t} = useTranslation();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState<string>("");
    const [passwordError, setPasswordError] = useState("");
    const [confirmPasswordError, setConfirmPasswordError] = useState("");
    const [consent, setConsent] = useState(false);
    const [isFetchingToken, setIsFetchingToken] = useState(false);
    const navigate = useNavigate();
    const [showPassword, setShowPassword] = useState(false);
    const {showSnackbar} = useSnackbar();

    const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setPassword(value);
        const err = validatePassword(value, t);
        setPasswordError(err || "");

        if (confirmPassword) {
            validateConfirmPassword(confirmPassword);
        }
    };

    const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;

        setConfirmPassword(value);
        validateConfirmPassword(value);
    };

    const validateConfirmPassword = (value: string) => {
        if (value !== password) {
            setConfirmPasswordError(t("passwordValidation.match") || "");
            return false;
        }
        setConfirmPasswordError("");
        return true;
    };

    const handleRegister = useCallback(async () => {
        if (!consent) return;
        if (passwordError) return;
        if (confirmPasswordError) return;

        setIsFetchingToken(true);
        try {
            const passed = await registerAdmin(email, password, confirmPassword, consent);
            if (passed) {
                showSnackbar(t('register.successMessage'), 'success');
                navigate("/thank-you-for-registration");
            } else {
                showSnackbar(t('register.errorMessage'), 'error');
            }
        } catch (error) {
            console.error('Error during registration:', error);
            showSnackbar(parseError(error), "error");
        } finally {
            setIsFetchingToken(false);
        }
    }, [consent, email, password, confirmPassword, passwordError, confirmPasswordError, showSnackbar, navigate, t]);

    return (
        <AuthContainer role="region" aria-labelledby="register-title">
            <LogoWithLanguageSwitcher src={Logo} alt={t('auth.logo.alt')} clickable={false}/>
            <StyledCard>
                <Header id="register-title">{t('register.admin.title')}</Header>
                <Subtext>{t('register.admin.description')}</Subtext>

                <StyledForm autoComplete="off" aria-live="polite" aria-atomic={true} aria-busy={isFetchingToken}>
                    <div>
                        <StyledTextField
                            label={t('register.email')}
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            fullWidth
                            disabled={isFetchingToken}
                            inputProps={{'aria-label': t('register.email.ariaLabel') as string}}
                        />
                    </div>
                    <div>
                        <StyledTextField
                            label={t('register.password')}
                            type={showPassword ? "text" : "password"}
                            autoComplete="new-password"
                            value={password}
                            onChange={handlePasswordChange}
                            onBlur={() => {
                                const error = validatePassword(password, t);
                                setPasswordError(error || "");
                            }}
                            error={!!passwordError}
                            helperText={passwordError}
                            fullWidth
                            disabled={isFetchingToken}
                            inputProps={{'aria-label': t('register.password.ariaLabel') as string}}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowPassword((prev) => !prev)}
                                            edge="end"
                                            sx={{color: 'primary.main'}}
                                            disabled={isFetchingToken}
                                            aria-label={t('register.password.toggleVisibility') as string}
                                        >
                                            {showPassword ? <VisibilityOff/> : <Visibility/>}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </div>
                    <div>
                        <StyledTextField
                            label={t('register.confirmPassword')}
                            type={showPassword ? "text" : "password"}
                            autoComplete="new-password"
                            value={confirmPassword}
                            onChange={handleConfirmPasswordChange}
                            onBlur={() => {
                                const error = validatePassword(confirmPassword, t);
                                setConfirmPasswordError(error || confirmPasswordError || "");
                            }}
                            error={!!confirmPasswordError}
                            helperText={confirmPasswordError}
                            fullWidth
                            disabled={isFetchingToken}
                            inputProps={{'aria-label': t('register.password.ariaLabel') as string}}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowPassword((prev) => !prev)}
                                            edge="end"
                                            sx={{color: 'primary.main'}}
                                            disabled={isFetchingToken}
                                            aria-label={t('register.password.toggleVisibility') as string}
                                        >
                                            {showPassword ? <VisibilityOff/> : <Visibility/>}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <div style={{height: '24px', marginTop: '4px'}}></div>
                    </div>
                    <CheckboxWrapper>
                        <Checkbox
                            checked={consent}
                            onChange={(e) => setConsent(e.target.checked)}
                            sx={{padding: "0 8px 0 0"}}
                            disabled={isFetchingToken}
                            inputProps={{'aria-label': t('register.consent.ariaLabel') as string}}
                        />
                        <CheckboxText>
                            {t('register.consent')}
                        </CheckboxText>
                    </CheckboxWrapper>
                    {isFetchingToken && (
                        <div role="status" aria-live="polite" style={{
                            position: 'absolute',
                            width: 1,
                            height: 1,
                            overflow: 'hidden',
                            clip: 'rect(1px, 1px, 1px, 1px)'
                        }}>
                            {t('register.loading')}
                        </div>
                    )}
                    <StyledButton onClick={handleRegister}
                                  disabled={!consent || isFetchingToken || !email || !password || !confirmPassword || !!passwordError || !!confirmPasswordError}>
                        {isFetchingToken ?
                            <CircularProgress size={24} sx={{color: 'inherit'}}/> : t('register.registerButton')}
                    </StyledButton>
                </StyledForm>
            </StyledCard>
        </AuthContainer>
    );
};

export default RegisterAdmin;
